import type { State } from '../store/types.js';

/**
 * Utility function to get the current section for a given panel dynamically.
 * This replaces the hardcoded switch statement in ScrollableSections.
 *
 * @param state - The store state
 * @param panel - The current panel value
 * @returns The current section for the panel
 */
export const getCurrentSectionForPanel = (state: State, panel: string): string => {
  // Handle hardcoded legacy panels first
  switch (panel) {
    case 'v': // Video
      return state.video.section;
    case 's': // System
      return state.system.section;
    case 'a': // Audio
      return state.audio.section;
    case 'n': // Network
      return state.network.section;
    case 'f': // Features
      return state.features.section;
    case 'mnt': // Monitoring
      return state.monitoring.section;
    case 'g': // General
      return state.general.section;
    case 'c': // Controls
      return state.controls.section;
    case 'm': // Manufacturer
      return 'General';
    default:
      // For dynamic section groups, check the sectionStates
      return state.sectionStates[panel]?.section || 'General';
  }
};

/**
 * Utility function to update the current section for a given panel dynamically.
 * This provides a centralized way to update section state.
 *
 * @param state - The store state (draft for Immer)
 * @param panel - The current panel value
 * @param section - The new section value
 */
export const updateSectionForPanel = (state: State, panel: string, section: string): void => {
  // Handle hardcoded legacy panels first
  switch (panel) {
    case 'v': // Video
      state.video.section = section;
      break;
    case 's': // System
      state.system.section = section;
      break;
    case 'a': // Audio
      state.audio.section = section;
      break;
    case 'n': // Network
      state.network.section = section;
      break;
    case 'f': // Features
      state.features.section = section;
      break;
    case 'mnt': // Monitoring
      state.monitoring.section = section;
      break;
    case 'g': // General
      state.general.section = section;
      break;
    case 'c': // Controls
      state.controls.section = section;
      break;
    case 'm': // Manufacturer
      // Manufacturer doesn't have sections, so do nothing
      break;
    default:
      // For dynamic section groups, update the sectionStates
      if (!state.sectionStates[panel]) {
        state.sectionStates[panel] = { section: 'General' };
      }
      state.sectionStates[panel]!.section = section;
      break;
  }
};

/**
 * Utility function to initialize section state for a dynamic panel if it doesn't exist.
 *
 * @param state - The store state (draft for Immer)
 * @param panel - The panel to initialize
 * @param defaultSection - The default section to set (defaults to 'General')
 */
export const initializeSectionStateForPanel = (
  state: State,
  panel: string,
  defaultSection: string = 'General'
): void => {
  // Only initialize for dynamic panels
  const hardcodedPanels = ['v', 's', 'a', 'n', 'f', 'mnt', 'g', 'c', 'm'];

  if (!hardcodedPanels.includes(panel) && !state.sectionStates[panel]) {
    state.sectionStates[panel] = { section: defaultSection };
  }
};
